import React, {useState, useRef, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {Heart, Cloud} from 'lucide-react';
import {loadShayariData, formatLegacyShayari} from '../utils/shayariData';
import {setupAudio, fadeAudio} from '../utils/audioManager';

const ShayariDisplay = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [showInput, setShowInput] = useState(false);
    const [verses, setVerses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const audioRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const loadVerses = async () => {
            try {
                setIsLoading(true);
                const data = await loadShayariData('current');
                setVerses(data || []);
            } catch (error) {
                console.error('Error loading verses:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadVerses();
        setupAudio(audioRef);

        // Add mouse movement tracking for effects
        const handleMouseMove = (e) => {
            if (containerRef.current) {
                const {clientX} = e;
                containerRef.current.style.setProperty('--mouse-x', `${clientX}px`);
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const handleHeartPattern = (e) => {
        if (e.detail === 2) {
            const handleKeyPress = (ke) => {
                if (ke.key === 'a') {
                    setShowInput(true);
                    document.removeEventListener('keydown', handleKeyPress);
                }
            };
            document.addEventListener('keydown', handleKeyPress);
        }
    };

    const handleAudioTransition = async () => {
        if (audioRef.current) {
            fadeAudio(audioRef, 'out', async () => {
                audioRef.current.pause();
                const revelationSound = new Audio('/audio/unlock/revelation.mp3');
                revelationSound.volume = 0.3;

                try {
                    await revelationSound.play();
                    revelationSound.onended = () => {
                        setupAudio(audioRef, 'legacy');
                        if (isPlaying) {
                            audioRef.current.play().catch(console.error);
                            fadeAudio(audioRef, 'in');
                        }
                    };
                } catch (error) {
                    console.error('Error playing revelation sound:', error);
                    setupAudio(audioRef, 'legacy');
                    if (isPlaying) {
                        audioRef.current.play().catch(console.error);
                        fadeAudio(audioRef, 'in');
                    }
                }
            });
        }
    };

    const checkPassword = async (input) => {
        const password = 'iloveamua';
        if (input === password) {
            const legacyVerses = await loadShayariData('legacy');
            const formattedVerses = legacyVerses.map(formatLegacyShayari);
            setVerses(formattedVerses);
            setIsUnlocked(true);
            handleAudioTransition();
            setShowInput(false);
        }
    };

    const toggleAudio = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            fadeAudio(audioRef, 'out', () => {
                audioRef.current.pause();
            });
        } else {
            if (audioRef.current.paused) {
                audioRef.current.play().catch(console.error);
                fadeAudio(audioRef, 'in');
            }
        }
        setIsPlaying(!isPlaying);
    };


    return (
        <div ref={containerRef}
             className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="gentle-cloud cloud-drift visible" style={{top: '10%', left: '10%'}}>
                    <Cloud className="w-12 h-12 text-white/10"/>
                </div>
                <div className="gentle-cloud floating visible" style={{top: '30%', right: '15%'}}>
                    <Cloud className="w-8 h-8 text-white/10"/>
                </div>
                <div className="gentle-cloud cloud-drift visible" style={{bottom: '20%', left: '20%'}}>
                    <Cloud className="w-10 h-10 text-white/10"/>
                </div>
                <div className="tear-trace" style={{left: '30%', height: '50%'}}/>
            </div>

            {/* Title Bar */}
            <div className="fixed top-0 left-0 w-full bg-blue-900/80 backdrop-blur-sm z-10">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center">
                        <img
                            src="/rose-heart.svg"
                            alt="Shayari Dil Se"
                            className="w-12 h-12 transition-transform duration-300 hover:scale-110"
                        />
                        <h1 className="ml-3 text-2xl font-bold text-white font-hindi">
                            {isUnlocked ? "Alfaaz ki Raat" : "Shayari Dil Se"}
                        </h1>
                    </div>
                    <Heart
                        className={`w-8 h-8 cursor-pointer transition-all duration-300 hover:scale-110 
                            ${isPlaying ? 'text-rose-400' : 'text-white'}`}
                        onClick={toggleAudio}
                        onDoubleClick={handleHeartPattern}
                    />
                </div>
            </div>

            {/* Content */}
            <div className="max-w-2xl mx-auto px-4 pt-24 pb-12">
                <AnimatePresence mode="popLayout">
                    {isLoading ? (
                        <motion.div
                            className="flex justify-center items-center h-96"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            exit={{opacity: 0}}
                        >
                            <div className="text-white text-xl loading-message font-hindi">
                                Loading Shayari...
                            </div>
                        </motion.div>
                    ) : (
                        <div className="space-y-16">
                            {verses.map((verse, index) => (
                                <motion.div
                                    key={verse.id}
                                    initial={{opacity: 0, y: 20}}
                                    animate={{opacity: 1, y: 0}}
                                    transition={{
                                        delay: index * 0.2,
                                        duration: 1.5,
                                        ease: [0.4, 0, 0.2, 1]
                                    }}
                                    className="verse-card silence-speaks verse-reveal visible"
                                >
                                    <div className="relative bg-white/10 backdrop-blur-md rounded-lg p-8
                                        border border-white/20 transition-all duration-500
                                        hover:bg-white/[0.15] hover:shadow-lg hover:-translate-y-1">
                                        {isUnlocked && verse.title && (
                                            <h2 className="text-xl text-white mb-4 font-semibold font-hindi">
                                                {verse.title}
                                            </h2>
                                        )}
                                        <p className="text-xl text-white whitespace-pre-line leading-relaxed font-hindi">
                                            {verse.text}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </AnimatePresence>
            </div>

            {/* Password Input */}
            <AnimatePresence>
                {showInput && (
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm
                            flex items-center justify-center z-50"
                    >
                        <input
                            type="password"
                            placeholder="..."
                            onChange={(e) => checkPassword(e.target.value)}
                            className="bg-transparent border-b-2 border-white/50 text-white
                                px-4 py-2 outline-none focus:border-white text-center text-xl
                                tracking-widest transition-colors duration-300"
                            autoFocus
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ShayariDisplay;